import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, withModifiers as _withModifiers, normalizeClass as _normalizeClass, vShow as _vShow, withDirectives as _withDirectives, unref as _unref, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-1e2e80ab"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "row" }
const _hoisted_2 = { class: "col-sm-12" }
const _hoisted_3 = { class: "invite-url-container" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = ["value"]
const _hoisted_6 = ["disabled"]
const _hoisted_7 = { class: "row nav-row" }
const _hoisted_8 = { class: "col-sm-8" }
const _hoisted_9 = {
  class: "nav nav-pills tab-list",
  role: "tablist"
}
const _hoisted_10 = { class: "row" }
const _hoisted_11 = { class: "col-sm-8 content-column" }

import { computed, ref, onMounted } from "vue";
import axios from "axios";
import moment from "dayjs";
import { debug as d } from "debug";
import { useVuelidate } from "@vuelidate/core";
import { maxLength, required } from "@vuelidate/validators";
import TeamsOrGroups from "../../typings/enums/teams-or-groups";
import { setTitle } from "../../services/utilities/set-title";
import {
  AccreditationStatusApiModel,
  GroupAndMembersApiModel,
  OrganisationAndMembersApiModel,
  OrganisationDescriptionApiModel,
  OrganisationMemberApiModel,
  OrganisationMemberApiRole,
  OrganisationMemberUpdateApiModel,
} from "../../api-models";
import OrganisationMemberMoveDialog from "../../components/admin/organisation/organisation-member-move-dialog.vue";
import AccreditedUserAreaOrganisationGroupAddDialog from "../../components/admin/organisation/accredited-user-area-organisation-group-add-dialog.vue";
import AccreditedUserAreaDataTab from "../../components/admin/organisation/accredited-user-area-data-tab.vue";
import AccreditedUserAreaTeamsTab from "../../components/admin/organisation/accredited-user-area-teams-tab.vue";
import AccreditedUserAreaAnalysisGroupsTab from "../../components/admin/organisation/accredited-user-area-analysis-groups-tab.vue";
import MembersTab from "../../components/admin/organisation/accredited-user-area-members-tab.vue";
import LoadingPage from "../loading-page.vue";
import organisationNameRegex from "../../services/utilities/organisation-name-regex";


export default /*#__PURE__*/_defineComponent({
  __name: 'accredited-user-area-organisation-detail-page',
  props: {
    id: {}
  },
  setup(__props: any) {

const debug = d("cs.AccreditedUserAreaOrganisationDetailPage");

function sortGroups(groups: GroupAndMembersApiModel[]): GroupAndMembersApiModel[] {
  return groups.slice().sort((a, b) => {
    if (a.group.name < b.group.name) {
      return -1;
    }
    if (a.group.name > b.group.name) {
      return 1;
    }

    return 0;
  });
}

const props = __props;

const model = ref<OrganisationAndMembersApiModel | null>(null);
const visibleOrganisations = ref<OrganisationDescriptionApiModel[]>([]);
const saveInProgress = ref(false);
const appliedFilter = ref<string | null>(null);
const memberMoveDialog = ref();
const groupAddDialog = ref();

const rules = {};

const state = {};

const v$ = useVuelidate(rules, state, { $autoDirty: true });

const sortedTeams = computed(() => {
  return model.value ? sortGroups(model.value.teams) : [];
});

const sortedAnalysisGroups = computed(() => {
  return model.value ? sortGroups(model.value.analysisGroups || []) : [];
});

const isLoadingInvite = ref(false);
const inviteUrl = ref('');
const inviteUrlInput = ref<HTMLInputElement | null>(null);
const isCopied = ref(false);

async function loadModel() {
  debug("loadModel started");
  saveInProgress.value = true;
  visibleOrganisations.value = (await axios.get("/api/accrediteduserarea/organisation")).data;
  model.value = (await axios.get(`/api/accrediteduserarea/organisation/${props.id}`)).data;
  saveInProgress.value = false;
  setTitle(`Organisation: ${props.id}`);
  debug("loadModel finished");
}

// In a real implementation, we would call an API to get the accreditation status
// For now, we'll just set it to true for testing purposes
async function loadAccreditationStatus() {
  try {
    const response = await axios.get(`/api/accrediteduserarea/organisation/accreditation/${props.id}`);
    isSuperAccredited.value = response.data.isSuperAccredited;
  } catch (e) {
    debug("Error loading accreditation status:", e);
    // Default to false if we can't determine the status
    isSuperAccredited.value = false;
  }
}

onMounted(async () => {
  debug("onMounted started");
  if (!props?.id) {
    return;
  }
  await loadModel();
  await loadAccreditationStatus();
  
  // Check for tab parameter in URL
  const urlParams = new URLSearchParams(window.location.search);
  const tabParam = urlParams.get('tab');
  if (tabParam && ['members', 'teams', 'analysisGroups', 'data'].includes(tabParam)) {
    currentTab.value = tabParam;
  }
  
  debug("onMounted finished");
});

async function revalidateData() {
  try {
    await loadModel();
  } catch (e) {
    debug(e);
  }
}

function changeRole(member: OrganisationMemberApiModel, newRole: OrganisationMemberApiRole) {
  if (member.role === newRole) {
    return;
  }

  saveInProgress.value = true;
  axios
    .post(
      "/api/accrediteduserarea/organisationmember/",
      {
        userId: member.shortUserDetails.id,
        organisationId: props.id,
        role: newRole,
      } as OrganisationMemberUpdateApiModel,
      {
        headers: { "content-type": "application/json" },
      },
    )
    .then((resp) => {
      saveInProgress.value = false;
      member.role = newRole;
    })
    .catch((error) => {
      alert("There was a problem changing the role, please try again.");
      saveInProgress.value = false;
    });
}

async function addGroups(teamsOrGroups) {
  debug("AddGroups");
  if (groupAddDialog.value) {
    const result = await groupAddDialog.value.show(
      model.value,
      teamsOrGroups,
      `Find ${teamsOrGroups === TeamsOrGroups.Teams ? "Team" : "Analysis Group"}`
    );
    if (result) await loadModel();
  }
}

// TAB SYSTEM
const currentTab = ref("members");

function selectMembersTab() {
  appliedFilter.value = null;
  currentTab.value = "members";
}

const isSuperAccredited = ref(false);

async function getInviteCode() {
  if (!model.value) return;
  
  isLoadingInvite.value = true;
  try {
    const response = await axios.get(`/api/accrediteduserarea/orginvite/${model.value.id}`);
    inviteUrl.value = response.data.inviteUrl;
  } catch (error) {
    console.error('Error getting invite code:', error);
    alert('Failed to get invite code. Please try again.');
  } finally {
    isLoadingInvite.value = false;
  }
}

async function copyToClipboard() {
  if (!inviteUrlInput.value) return;
  
  try {
    await navigator.clipboard.writeText(inviteUrlInput.value.value);
    isCopied.value = true;
    setTimeout(() => {
      isCopied.value = false;
    }, 2000);
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    alert('Failed to copy to clipboard. Please try again.');
  }
}

return (_ctx: any,_cache: any) => {
  return (model.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["organisation-details", { updating: saveInProgress.value }])
      }, [
        _createVNode(OrganisationMemberMoveDialog, {
          ref_key: "memberMoveDialog",
          ref: memberMoveDialog
        }, null, 512),
        _createVNode(AccreditedUserAreaOrganisationGroupAddDialog, {
          ref_key: "groupAddDialog",
          ref: groupAddDialog
        }, null, 512),
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("h1", null, _toDisplayString(model.value.name), 1),
            _createElementVNode("div", _hoisted_3, [
              (!inviteUrl.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    class: "btn btn-primary",
                    onClick: getInviteCode,
                    disabled: isLoadingInvite.value
                  }, _toDisplayString(isLoadingInvite.value ? 'Loading...' : 'Get Invite Link'), 9, _hoisted_4))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createElementVNode("input", {
                      type: "text",
                      class: "form-control",
                      value: inviteUrl.value,
                      readonly: "",
                      ref_key: "inviteUrlInput",
                      ref: inviteUrlInput
                    }, null, 8, _hoisted_5),
                    _createElementVNode("button", {
                      class: "btn btn-default",
                      onClick: copyToClipboard,
                      disabled: isCopied.value
                    }, _toDisplayString(isCopied.value ? 'Copied!' : 'Copy to Clipboard'), 9, _hoisted_6)
                  ], 64))
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("ul", _hoisted_9, [
              _createElementVNode("li", {
                role: "presentation",
                class: _normalizeClass({ active: currentTab.value === 'members' })
              }, [
                _createElementVNode("a", {
                  href: "#",
                  role: "tab",
                  "data-test": "members-tab",
                  onClick: _withModifiers(selectMembersTab, ["prevent"])
                }, "Members (" + _toDisplayString(model.value.members.length) + ")", 1)
              ], 2),
              _createElementVNode("li", {
                role: "presentation",
                class: _normalizeClass({ active: currentTab.value === 'teams' })
              }, [
                _createElementVNode("a", {
                  href: "#",
                  role: "tab",
                  "data-test": "teams-tab",
                  onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (currentTab.value = 'teams'), ["prevent"]))
                }, "Teams (" + _toDisplayString(model.value.teams.length) + ")", 1)
              ], 2),
              _createElementVNode("li", {
                role: "presentation",
                class: _normalizeClass({ active: currentTab.value === 'analysisGroups' })
              }, [
                _createElementVNode("a", {
                  href: "#",
                  role: "tab",
                  "data-test": "org-analysis-groups-tab",
                  onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (currentTab.value = 'analysisGroups'), ["prevent"]))
                }, "Analysis Groups (" + _toDisplayString(model.value.analysisGroups.length) + ")", 1)
              ], 2),
              (isSuperAccredited.value)
                ? (_openBlock(), _createElementBlock("li", {
                    key: 0,
                    role: "presentation",
                    class: _normalizeClass({ active: currentTab.value === 'data' })
                  }, [
                    _createElementVNode("a", {
                      href: "#",
                      role: "tab",
                      "data-test": "org-data-tab",
                      onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (currentTab.value = 'data'), ["prevent"]))
                    }, "Data")
                  ], 2))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createElementVNode("div", _hoisted_11, [
            _withDirectives(_createVNode(MembersTab, {
              model: model.value,
              onRevalidateOrgData: revalidateData
            }, null, 8, ["model"]), [
              [_vShow, currentTab.value === 'members']
            ]),
            _withDirectives(_createVNode(AccreditedUserAreaTeamsTab, {
              id: _ctx.id,
              model: model.value,
              teamsOrGroups: _unref(TeamsOrGroups),
              canCreateGroupsAndTeams: true,
              sortedTeams: sortedTeams.value,
              onAddGroups: addGroups
            }, null, 8, ["id", "model", "teamsOrGroups", "sortedTeams"]), [
              [_vShow, currentTab.value === 'teams']
            ]),
            _withDirectives(_createVNode(AccreditedUserAreaAnalysisGroupsTab, {
              id: _ctx.id,
              model: model.value,
              teamsOrGroups: _unref(TeamsOrGroups),
              canCreateGroupsAndTeams: true,
              sortedAnalysisGroups: sortedAnalysisGroups.value,
              onAddGroups: addGroups
            }, null, 8, ["id", "model", "teamsOrGroups", "sortedAnalysisGroups"]), [
              [_vShow, currentTab.value === 'analysisGroups']
            ]),
            (isSuperAccredited.value)
              ? _withDirectives((_openBlock(), _createBlock(AccreditedUserAreaDataTab, {
                  key: 0,
                  model: model.value,
                  onRevalidateOrgData: revalidateData
                }, null, 8, ["model"])), [
                  [_vShow, currentTab.value === 'data']
                ])
              : _createCommentVNode("", true)
          ])
        ])
      ], 2))
    : (_openBlock(), _createBlock(LoadingPage, { key: 1 }))
}
}

})